import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

type SerializationParams<T> = {
  urlKey?: string;
  urlSerialization?: {
    serialize: (value: T) => string;
    parse: (value: string) => T;
  };

  storageKey?: string;
  localStorageSerialization?: {
    serialize: (value: T) => string;
    parse: (value: string) => T;
  };
};

const defaultSerialization = { serialize: JSON.stringify, parse: JSON.parse };

/**
 * Sync a state between the localStorage and the Search Params
 * The precedence is Search Params > localStorage
 */
export const usePersistedState = <T,>(
  initialValue: T,
  {
    urlKey,
    urlSerialization = defaultSerialization,
    storageKey,
    localStorageSerialization = defaultSerialization,
  }: SerializationParams<T>,
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const setUrlValue = useCallback(
    (value?: T) => {
      if (urlKey == undefined) return;

      // delete the search params when it's undefined
      if (value === undefined) {
        setSearchParams((params) => {
          params.delete(urlKey);
          return params;
        });
        return;
      }

      try {
        const serializedForUrl = urlSerialization.serialize(value);
        setSearchParams((p) => {
          p.set(urlKey, serializedForUrl);
          return p;
        });
      } catch (e) {
        console.error("error while serializing url params for", urlKey);
        console.error(e);
      }
    },
    [setSearchParams, urlKey, urlSerialization],
  );

  const setLocalStorageValue = useCallback(
    (value: T) => {
      // delete the local storage when it's undefined
      if (value === undefined) {
        if (storageKey != undefined) localStorage.removeItem(storageKey);
        return;
      }

      // update the storage / url params when it's defined
      if (storageKey != undefined) {
        try {
          const localStorageSerialized =
            localStorageSerialization.serialize(value);
          localStorage.setItem(storageKey, localStorageSerialized);
        } catch (e) {
          console.error("error while serializing localStorage for", storageKey);
          console.error(e);
        }
      }
    },
    [storageKey, localStorageSerialization],
  );

  function getStorageValue() {
    if (storageKey == undefined) return undefined;

    const localStorageValue = localStorage.getItem(storageKey);

    if (localStorageValue == undefined) return undefined;

    try {
      return localStorageSerialization.parse(localStorageValue);
    } catch (e) {
      console.error("error while parsing localStorage for", storageKey);
      console.error(e);

      localStorage.removeItem(storageKey);
    }
    return undefined;
  }

  function getUrlParamValue() {
    if (urlKey == undefined) return undefined;

    const urlParamValue = searchParams.get(urlKey);

    if (urlParamValue == undefined) return undefined;

    try {
      return urlSerialization.parse(urlParamValue);
    } catch (e) {
      console.error("error while parsing url params for", urlKey);
      console.error(e);

      searchParams.delete(urlKey);
    }
    return undefined;
  }

  function getInitialValue() {
    const urlValue = getUrlParamValue();
    if (urlValue != undefined) return urlValue;
    return getStorageValue() ?? initialValue;
  }

  const [state, setState] = useState<T>(getInitialValue);

  // we use a use effect for the UrlValue, because we can't set a url param when the component is mounting
  useEffect(() => {
    setUrlValue(state);
    setLocalStorageValue(state);
  }, [state, setUrlValue, setLocalStorageValue]);

  return [state, setState] as const;
};
